import { useSortable } from '@dnd-kit/react/sortable';
import classNames from 'classnames';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useCloseTab } from 'routes/essential-workspace/components/header/hooks';
import { QuoteStatus } from 'routes/essential-workspace/components/quote-status';
import { Price } from 'routes/essential-workspace/components/side-panel/tab/price';
import { Product } from 'routes/essential-workspace/components/side-panel/tab/product';
import { SolveFor } from 'routes/essential-workspace/components/side-panel/tab/solve-for';
import { useEssentialTabName } from 'routes/essential-workspace/hooks/use-tab-name';

import './style.scss';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { FormIdProps } from 'shared/props';
import { workspaceTabActivated } from 'state/workspaces/workspace.actions';

function useTab(formId: string) {
  const dispatch = useThunkDispatch();
  const activeTabId = useAssertedActiveTabId();
  const isActive = activeTabId === formId;

  const activate = () => {
    if (!isActive) {
      dispatch(workspaceTabActivated(formId, 'essential', 'quote'));
      logAnalytics.trackEvent('Primary market essential', 'Change tab');
    }
  };
  return { activate, isActive };
}

export type TabProps = FormIdProps & {
  index: number;
};

export function Tab({ formId, index }: TabProps) {
  const { activate, isActive } = useTab(formId);
  const closeTab = useCloseTab();

  const { ref, handleRef } = useSortable({
    id: formId,
    index: index,
    type: 'quote',
  });

  const handleDelete = () => {
    closeTab(formId);
  };

  const tabName = useEssentialTabName(formId);

  return (
    <li
      ref={ref}
      role="listitem"
      className={classNames(
        'quote-tab-item list-group-item list-group-item-secondary list-group-item-action flex-x border-bottom border-opacity-40 px-2 gap-12px overflow-hidden tab-min-w-0',
        { active: isActive },
      )}
      aria-label={`tab name : ${tabName}`}
      onClick={activate}
    >
      <QuoteStatus formId={formId} variant="chip" className="flex-shrink-0" />
      <div className="flex-grow-1 flex-y gap-1 tab-min-w-0">
        <h2 className={classNames('fs-5 lh-1', { 'text-primary': isActive })}>{tabName}</h2>
        <Product formId={formId} />
      </div>
      <div className="flex-shrink-0 flex-y align-items-end gap-1">
        <SolveFor formId={formId} />
        <Price formId={formId} />
      </div>

      <div className="quote-tab-item--actions">
        <button className="text-body-secondary quote-tab-delete btn btn-link p-1" type="button" onClick={handleDelete}>
          <i className="icon icon-outlined">close</i>
        </button>
        <i className={classNames('icon icon-outlined drag-handle')} ref={handleRef}>
          drag_indicator
        </i>
      </div>
    </li>
  );
}
